/**
 * UpdateThrottler — Counter-Based Per-Subsystem Update Rate Throttling
 *
 * Adapted from AstroMonix LOD: each subsystem gets its own counter and
 * only updates when the counter reaches its threshold. This prevents
 * expensive operations from running every frame at lower quality tiers.
 *
 * Also provides time-based throttling for visual effects (frame-rate independent).
 */

export interface ThrottleState {
  /** Whether LOD/quality check should run this frame */
  updateLOD: boolean;
  /** Whether label positions should update this frame */
  updateLabels: boolean;
  /** Whether rotation should update this frame */
  updateRotation: boolean;
  /** Whether physics should update this frame */
  updatePhysics: boolean;
  /** Whether stats should update this frame */
  updateStats: boolean;
  /** Whether atmosphere effects should render this frame */
  renderAtmosphere: boolean;
  /** Whether energy fields should render this frame */
  renderEnergyFields: boolean;
}

export interface UpdateIntervals {
  lod: number;
  labels: number;
  rotation: number;
  physics: number;
  stats: number;
}

export class UpdateThrottler {
  private _counters = { lod: 0, labels: 0, rotation: 0, physics: 0, stats: 0 };
  private _intervals: UpdateIntervals = { lod: 5, labels: 10, rotation: 1, physics: 2, stats: 10 };

  // Time-based throttling for visual effects
  private _lastEffectTimes: Record<string, number> = {};
  private _effectIntervals: Record<string, number> = {
    'atmosphere': 160,   // Background gradient ~6fps
    'energyFields': 100, // Energy fields ~10fps
    'lightning': 67,     // Lightning ~15fps
    'particleUpdate': 50 // Particle updates ~20fps
  };

  /** Set update intervals from quality tier */
  setIntervals(intervals: UpdateIntervals): void {
    this._intervals = intervals;
  }

  /**
   * Tick all counters. Returns which subsystems should update this frame.
   * Call once per frame before any subsystem work.
   */
  tick(): ThrottleState {
    const c = this._counters;
    const i = this._intervals;

    c.lod++; c.labels++; c.rotation++; c.physics++; c.stats++;

    const result: ThrottleState = {
      updateLOD:       c.lod      >= i.lod,
      updateLabels:    c.labels   >= i.labels,
      updateRotation:  c.rotation >= i.rotation,
      updatePhysics:   c.physics  >= i.physics,
      updateStats:     c.stats    >= i.stats,
      renderAtmosphere: true,  // Default: render
      renderEnergyFields: true
    };

    // Reset counters that fired
    if (result.updateLOD)       c.lod       = 0;
    if (result.updateLabels)    c.labels    = 0;
    if (result.updateRotation)  c.rotation  = 0;
    if (result.updatePhysics)   c.physics   = 0;
    if (result.updateStats)     c.stats     = 0;

    return result;
  }

  /**
   * Time-based throttle check for visual effects.
   * @param effectKey - Identifier for the effect
   * @param customIntervalMs - Optional custom interval override
   * @param now - Current timestamp (ms)
   * @returns Whether the effect should fire this frame
   */
  shouldFireEffect(effectKey: string, customIntervalMs?: number, now?: number): boolean {
    const t = now ?? performance.now();
    const interval = customIntervalMs ?? this._effectIntervals[effectKey] ?? 50;
    const last = this._lastEffectTimes[effectKey] ?? 0;
    if (t - last >= interval) {
      this._lastEffectTimes[effectKey] = t;
      return true;
    }
    return false;
  }

  /**
   * Check if an effect should fire, with an external guard (like panel-busy + catchup).
   * Returns true if: (throttle interval passed AND not panelBusy) OR catchup flag is set.
   */
  shouldFireEffectGuarded(
    effectKey: string,
    panelBusy: boolean,
    catchup: boolean,
    customIntervalMs?: number,
    now?: number
  ): boolean {
    const t = now ?? performance.now();
    const interval = customIntervalMs ?? this._effectIntervals[effectKey] ?? 50;

    if (catchup) {
      this._lastEffectTimes[effectKey] = t;
      return true;
    }

    if (panelBusy) return false;

    const last = this._lastEffectTimes[effectKey] ?? 0;
    if (t - last >= interval) {
      this._lastEffectTimes[effectKey] = t;
      return true;
    }
    return false;
  }

  /** Reset all counters */
  reset(): void {
    this._counters = { lod: 0, labels: 0, rotation: 0, physics: 0, stats: 0 };
    this._lastEffectTimes = {};
  }

  /** Get current counter values (for debugging) */
  getCounters(): Readonly<typeof this._counters> {
    return { ...this._counters };
  }
}

// Singleton
let _utInst: UpdateThrottler | null = null;
export function getUpdateThrottler(): UpdateThrottler {
  if (!_utInst) _utInst = new UpdateThrottler();
  return _utInst;
}
